import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux, useDispatch } from 'react-redux'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { getBoundActions } from '../../../actions/index'
import { DETAIL_PAGE_STATIC_KEY } from '../../../services/detail/index'
import { getPrimary } from '../../../services/languages/index'
import Link from '../../Link/index'
import { H6 } from '../../Heading/index'
import { formatDuration } from '../../../theme/web-app/index'
import { parse as parseQuery, stringify as stringifyQuery } from '../../../services/query-string/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import PlaylistAddRemove from '../../PlaylistAddRemove/index'
import ProgressBar, { PROGRESS_BAR_TYPE_ROUNDED_GRADIENT } from '../../ProgressBar/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../../Picture/index'
import { URL_GAIA_PLUS } from '../../../services/url/constants'
import { PLAY_CLICK_GA4_EVENT, CLICK_TO_DETAILS_PAGE_GA4_EVENT } from '../../../services/event-tracking/index'
import {
  isSeries,
  isGuide,
  getContentTags,
  isGaiaPlusFeature,
  getFeaturedContent,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
  isEpisode,
  isSubtypeEvent,
} from '../../../services/content/index'
import {
  isYogaOrMeditation,
  onClickDetailPageTag,
} from '../utils'
/**
 *
 * @param {Object} props - The props
 * @param {String} props.type - Type of the icon for the button
 * @param {String} props.classes - String with classes for the circle button
 * @returns {JSX.Element} Circle button for actions
 */
function CircleButton (props) {
  const { type, classes } = props
  return (
    <div className="detail-episode-list__circle-button">
      <IconV2
        type={type}
        className={classes}
      />
    </div>
  )
}

/**
 *
 * @param {Object} props - The props
 * @param {String} props.image - background image for the video
 * @returns {JSX.Element} Image of the current item
 */
function DetailEpisodeImage (props) {
  const {
    item,
    isGuideDetailPage,
    ga4EventData,
    isEvent,
  } = props
  const featureMedia = item.get('featureMedia', Map()) || Map()
  const userMediaPosition = featureMedia.getIn(['userMediaPosition', 'position'], 0) || 0
  const duration = featureMedia.get('duration', 0) || 0
  const image = item.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_KEYART_NO_TEXT) || Map()
  const hasProgressBar = Map.isMap(item.get('featureMedia')) && item.getIn(['featureMedia', 'userMediaPosition', 'position'], 0) > 0
  const isLocked = !featureMedia.getIn(['contentPolicy', 'accessCheck', 'allowed']) || false

  const isGuideOrEvent = isGuideDetailPage || isEvent
  const imageWidth = '525'
  let imageUrl = ''
  let fallbackImageUrl = ''

  let childClassName = 'detail-episode-list__item-image-background'

  if (isLocked) {
    childClassName += ' detail-episode-list__item-image-background--lock'
  }

  try {
    const fallbackImageUrlObject = new URL(image.get('url'))
    const imageUrlObject = new URL(image.get('url'))
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: imageWidth,
      fm: 'jpg',
      compress: 'auto',
    }
    const imageOptions = {
      ...parsedImageQuery,
      w: imageWidth,
      fm: 'webp',
      q: '100',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedImageQuery = stringifyQuery(imageOptions)

    // set the search string back onto the fallback URL
    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

    // set the search string back onto the image URL
    _set(imageUrlObject, 'search', stringifiedImageQuery)

    fallbackImageUrl = fallbackImageUrlObject.toString()
    imageUrl = imageUrlObject.toString()
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  return (
    <>
      <div className="detail-episode-list__item-image">
        <div
          className={childClassName}
        >
          {isGuideOrEvent
            ? <DetailEpisodeAccessOverlay
              isLocked={isLocked}
              ga4EventData={ga4EventData}
              item={item}
              {...props}
            />
            : <DetailEpisodeImageActions
              isLocked={isLocked}
              ga4EventData={ga4EventData}
              {...props}
              item={item}
            />
          }
          {hasProgressBar && (
            <div className="detail-episode-list__item-progress-bar">
              <ProgressBar
                duration={duration}
                userInfo={Map({ featurePosition: userMediaPosition })}
                type={PROGRESS_BAR_TYPE_ROUNDED_GRADIENT}
              />
            </div>
          )}
          <Picture
            fallbackSrc={fallbackImageUrl}
            width={'400'}
            height={'225'}
            alt={''}
            className="detail-episode-list__picture"
            imageClassName="detail-episode-list__item-image-optimized"
            loading={IMAGE_LOADING_LAZY}
            sources={[
              {
                type: IMAGE_TYPE_WEBP,
                srcSet: imageUrl,
              },
            ]}
          />
        </div>
      </div>
    </>
  )
}

/**
 * Overlay for the episode list image area
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {Boolean} props.isLocked - Is feature locked to watch
 * @param {Map} props.ga4EventData - The data for GA4 event
 * @param {Function} props.setEventDefault - Redux action to send GA data
 * @returns {JSX.Element} Overlay for the image of the current item
 */
function DetailEpisodeAccessOverlay (props) {
  const {
    item = Map(),
    isLocked,
    ga4EventData,
    setEventDefault,
  } = props

  const url = item.get('path')
  const featureMedia = item.get('featureMedia', Map()) || Map()
  const geoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'])

  const handleGaEventPlayClicked = () => {
    // GA4 events
    const playClickEventData = PLAY_CLICK_GA4_EVENT
      .mergeDeep({
        ...ga4EventData,
        ui_element: 'Detail Page Episode List',
      })
    setEventDefault(playClickEventData)
  }

  if (geoAllowed) {
    return (isLocked
      ? (
        <div className="detail-episode-list__item-image-access-overlay">
          <div className="detail-episode-list__item-image-access-overlay-background"></div>
          <div className="detail-episode-list__item-image-access-overlay-padlock"></div>
        </div>
      ) : (
        <Link
          to={`${url}?fullplayer=feature`}
          className="detail-episode-list__item-image-access-overlay"
          onClick={handleGaEventPlayClicked}
        />
      )
    )
  }

  return null
}

/**
 * Actions for the episode list image area
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {Map} props.ga4EventData - The data for GA4 event
 * @param {Boolean} props.isTouchable - If user's device is touchable
 * @param {Function} props.setEventDefault - Redux action to send GA data
 * @param {Boolean} props.isLocked - Is feauter locked to watch
 * @returns {JSX.Element} Actions for the image of the current item
 */
function DetailEpisodeImageActions (props) {
  const {
    item = Map(),
    isLocked,
    ga4EventData,
    isTouchable,
    setEventDefault,
  } = props
  const url = item.get('path')
  const id = item.get('contentId')
  const title = item.get('title')
  const featureMedia = item.get('featureMedia', Map()) || Map()
  const isLiveAccessEpisode = isGaiaPlusFeature(featureMedia)
  const geoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'])

  const handleGaEventPlayClicked = () => {
    // GA4 events
    const playClickEventData = PLAY_CLICK_GA4_EVENT
      .mergeDeep({
        ...ga4EventData,
        ui_element: 'Detail Page Episode List',
      })
    setEventDefault(playClickEventData)
  }

  const renderLock = () => {
    if (isLiveAccessEpisode) {
      return (
        <Link
          to={URL_GAIA_PLUS}
          className="detail-episode-list__link">
          <CircleButton
            classes="detail-episode-list__circle-button-padlock"
            type={ICON_TYPES.PADLOCK}
          />
        </Link>
      )
    }
    return (
      <CircleButton
        classes="detail-episode-list__circle-button-padlock"
        type={ICON_TYPES.PADLOCK}
      />
    )
  }

  if (geoAllowed) {
    return (
      <div className={`detail-episode-list__item-image-actions${isTouchable ? ' detail-episode-list__item-image-actions--touchable' : ''}`}>
        <div className="detail-episode-list__item-image-buttons detail-episode-list__item-image-buttons--center">
          {isLocked ? renderLock() : (
            <Link
              to={`${url}?fullplayer=feature`}
              className="detail-episode-list__link"
              onClick={handleGaEventPlayClicked}>
              <CircleButton
                classes="detail-episode-list__circle-button-play"
                type={ICON_TYPES.PLAY}
              />
            </Link>
          )}
        </div>
        <div className="detail-episode-list__item-image-buttons detail-episode-list__item-image-buttons--bottom-right">
          <PlaylistAddRemove
            className="detail-episode-list__item-image-buttons detail-episode-list__item-image-buttons--bottom-right"
            contentId={id}
            contentTitle={title}
            ga4EventData={{
              ...ga4EventData,
              ui_element: 'Detail Page Episode List',
            }}
          />
        </div>
      </div>
    )
  }

  return null
}

/**
 * List of tags for detail episode list
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {String} props.uiLocation - The ui location
 * @param {String} props.userLanguage - The primary language of the user
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Episode list tags component
 */
function EpisodeListTags (props) {
  const {
    item,
    uiLocation,
    userLanguage,
    staticText,
  } = props
  const references = item.get('references', List()) || List()
  const contentType = item.get('contentType')
  const contentSubtype = item.get('contentSubtype')
  const title = item.get('title')
  const id = item.get('contentId')
  const url = item.get('path')
  const isNew = item.get('isNew')
  const displayType = item.get('displayType')
  const tags = getContentTags({
    tags: references,
    url,
    contentSubtype,
    isNew,
    contentType,
    staticText,
    userLanguage,
    displayType,
  }) || List()
  const dispatch = useDispatch()

  if (tags.size <= 0) {
    return null
  }

  return (
    <div className="detail-episode-list__tags">
      {
        tags.map((t) => {
          if (!Map.isMap(t)) {
            return null
          }

          const type = t.get('type')

          return (
            <React.Fragment key={`${t}-${id}`}>
              {
                t.get('path')
                  ? <Link
                      to={t.get('path')}
                      className={`detail-episode-list__tag detail-episode-list__tag--linked${type ? ` detail-episode-list__tag--${type}` : ''}`}
                      onClick={() => onClickDetailPageTag({
                        data: item,
                        tagId: t.get('contentId'),
                        tagName: t.get('title') || t.get('name'),
                        id,
                        contentType,
                        title,
                        dispatch,
                        uiLocation,
                      })}
                    >
                      {t.get('title') || t.get('name')}
                    </Link>
                  : <span className={`detail-episode-list__tag${type ? ` detail-episode-list__tag--${type}` : ''}`}>
                      {t.get('title') || t.get('name')}
                    </span>
              }
            </React.Fragment>
          )
        })
      }
    </div>
  )
}

/**
 *
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {Boolean} props.isGuideDetailPage - If the current page is a guide detail page
 * @param {Map} props.ga4EventData - Url to the current video page
 * @param {Function} props.setEventDefault - Redux action for setting GA data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Details of the current item
 */
function DetailEpisodeData (props) {
  const {
    item,
    isGuideDetailPage,
    ga4EventData,
    setEventDefault,
    staticText,
  } = props
  const title = item.get('title')
  const url = item.get('path')
  const description = item.get('shortDescription')
  const episode = item.get('episodeNumber')

  const handleGaEventTitleClicked = () => {
    // GA4 events
    const clickToDetailsPageEventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
      ...ga4EventData,
      ui_element: 'Title',
    })
    setEventDefault(clickToDetailsPageEventData)
  }

  return (
    <div className="detail-episode-list__item-details">
      <div className="detail-episode-list__item-info">
        <Link
          to={url}
          className="detail-episode-list__link"
          onClick={handleGaEventTitleClicked}
        >
          <H6 className="detail-episode-list__item-title">
            {
              isGuideDetailPage
                ? <><span className="detail-episode-list__item-title--subtitle">{staticText.get('lesson')} {episode}:</span> {title}</>
                : `${episode}. ${title}`
            }
          </H6>
        </Link>
        <DetailEpisodeItemMeta
          item={item}
          {...props}
        />
        <div className="detail-episode-list__item-description">{description}</div>
      </div>
      <EpisodeListTags
        item={item}
        {...props}
      />
    </div>
  )
}

/**
 *
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Meta data for the current video item
 */
function DetailEpisodeItemMeta (props) {
  const {
    item,
    staticText,
  } = props
  const featureMedia = item.get('featureMedia', Map()) || Map()
  const duration = featureMedia.get('duration', 0) || 0
  const guest = item.get('cast')
  const host = item.get('instructor')
  const contentSubtype = item.get('contentSubtype')
  const showHost = isYogaOrMeditation(contentSubtype) && !guest

  return (
    <div className="detail-episode-list__item-meta">
      {duration && (
        <div className="detail-episode-list__item-meta-duration">
          {formatDuration(duration)}
        </div>
      )}
      {guest && (
        <div className="detail-episode-list__item-meta-guest">
          {staticText.getIn(['featuring'])}: {guest}
        </div>
      )}
      {host && showHost && (
        <div className="detail-episode-list__item-meta-guest">
          {staticText.getIn(['teacher'])}: {host}
        </div>
      )}
    </div>
  )
}

/**
 * The placeholder to show for the episode list if there is no data
 * @returns {JSX.Element} Meta data for the current video item
 */
function DetailEpisodeListPlaceholder () {
  const placeholderList = List([1, 2, 3, 4])

  return (
    <div className="detail-episode-list">
      {placeholderList.map((p, i) => {
        return (
          <div className="detail-episode-list__item" key={`detail-episode-list-placeholder-${i}`}>
            <div className="detail-episode-list__item-image--placeholder" />
            <div className="detail-episode-list__item-details">
              <div className="detail-episode-list__item--text-placeholder" />
              <div className="detail-episode-list__item--text-placeholder" />
              <div className="detail-episode-list__item--text-placeholder" />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 *
 * @param {Object} props - The props
 * @param {Map} props.item - The episode data
 * @param {Map} props.detailData - The data for the current detail page
 * @param {String}  props.uiLocation - The ui location
 * @param {Boolean} props.isSeriesDetailPage - If the current page is a series detail page
 * @returns {JSX.Element} Item with the information, image and action of the item
 */
function DetailEpisodeItem (props) {
  const {
    item,
    detailData,
    uiLocation,
    isSeriesDetailPage,
  } = props
  const parentSeriesId = isSeriesDetailPage ? detailData.get('contentId') : detailData.get('seriesId')
  const parentSeriesName = isSeriesDetailPage ? detailData.get('title') : detailData.get('seriesTitle')
  const featuredEpisode = isSeriesDetailPage ? getFeaturedContent({ data: detailData }) : detailData

  const isActive = item.get('contentId') === featuredEpisode.get('contentId', -1)
  let className = 'detail-episode-list__item'

  if (isActive) {
    className += ' detail-episode-list__item--active'
  }

  const ga4EventData = {
    media_id: item.getIn(['featureMedia', 'mediaId']),
    content_id: item.get('contentId'),
    content_name: item.get('title'),
    content_type: item.get('contentType'),
    media_language: item.get('languageFullText'),
    is_featured_media: true,
    parent_series_id: parentSeriesId,
    parent_series_name: parentSeriesName,
    ui_location: uiLocation,
  }

  return (
    <div className={className}>
      <DetailEpisodeImage
        ga4EventData={ga4EventData}
        item={item}
        {...props}
      />
      <DetailEpisodeData
        item={item}
        ga4EventData={ga4EventData}
        {...props}
      />
    </div>
  )
}

function DetailEpisodeListGraphQL (props) {
  const {
    episodeData = List(),
  } = props

  if (!List.isList(episodeData) || !episodeData.size) {
    return <DetailEpisodeListPlaceholder />
  }

  if (List.isList(episodeData)) {
    return (
      <div className="detail-episode-list">
        {episodeData.map((item, index) => (
          <DetailEpisodeItem
            key={`${item.get('contentId')}_${item.get('episodeNumber', index)}`}
            item={item}
            {...props}
          />
        ))}
      </div>
    )
  }

  return null
}

DetailEpisodeListGraphQL.propTypes = {
  detailData: ImmutablePropTypes.map.isRequired,
  episodeData: ImmutablePropTypes.list.isRequired,
  uiLocation: PropTypes.string.isRequired,
  isTouchable: PropTypes.bool,
  userLanguage: PropTypes.string,
  isSeriesDetailPage: PropTypes.bool,
  isGuideDetailPage: PropTypes.bool,
  setEventDefault: PropTypes.func,
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
        app,
        user,
      } = state
      const { detailData } = props
      const contentType = detailData.get('contentType')
      const contentSubtype = detailData.get('contentSubtype')
      const displayType = detailData.get('displayType')
      const seriesContentSubtype = detailData.get('seriesContentSubtype')
      const isEpisodeDetailPage = isEpisode({ contentType, data: detailData })
      const isEvent = isEpisodeDetailPage
        ? isSubtypeEvent({ contentSubtype: seriesContentSubtype })
        : isSubtypeEvent({ contentSubtype })

      return {
        auth,
        staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
        isTouchable: app.getIn(['viewport', 'touchable'], false),
        userLanguage: getPrimary(user.getIn(['data', 'language'])),
        isSeriesDetailPage: isSeries({ contentType }),
        isGuideDetailPage: isGuide({ contentSubtype, displayType }),
        isEvent,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(DetailEpisodeListGraphQL)
